/// <amd-module name="Test/TestTabInPipeline/Service/TestResultsTabProviderService" />

import React = require("react");

import { ObservableArray, IReadonlyObservableArray } from "VSS/Core/Observable";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import { Services, VssService, IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssContributedTab } from "VSSUI/Tabs.Types";
import * as Resources from "Test/TestTabInPipeline/Resources";
import { IRunViewContributedTabProps } from 'Build/Run/RunViewContributedTab';
import { IViewContextData, ViewContextStatus, IData, ViewContext, ContributionSource } from 'Test/common-util/Common';
import { BuildStatus } from 'Build/Client/Contracts/Build';
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { TestResultsView } from 'Test/TestResultsView/Components/TestResultsView';
import { ScreenSize } from "VSS/Core/Util/Screen";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

export class TestResultsTabProviderService extends VssService implements ITabService<IRunViewContributedTabProps> {
    public getTabs(tabId: string, contextualData?: IRunViewContributedTabProps): IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> {
        let isCompleted = false;
        if (!!contextualData) {
            this._contextualData = contextualData;
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            let artifact: IViewContextData = this._getArtifactData(contextualData);
            if (artifact.status === ViewContextStatus.Completed) {
                if(!this._shouldUsePipelineAPi()) {
                    const syncData: TCMContracts.TestResultSummary | undefined = contributionService.getData<TCMContracts.TestResultSummary>(this._buildSummaryDataProviderId);
                    if (syncData) {
                        if (syncData.totalRunsCount > 0) {
                            this._tab.render = () => this._getTestResultsView(artifact);
                            return [this._tab];
                        }
                    }
                }
                else {
                    const syncData: TCMContracts.PipelineTestMetrics | undefined = contributionService.getData<TCMContracts.PipelineTestMetrics>(this._pipelineSummaryDataProviderId);
                    if (syncData && syncData.runSummary && syncData.runSummary.totalRunsCount > 0) {
                        this._tab.render = () => this._getTestResultsView(artifact);
                        return [this._tab];
                    }
                }
                isCompleted = true;
            }

            this._contextualData.notifyUpdated.subscribe(this._showTestTab);
            this._checkAndAddTab(artifact, contributionService, isCompleted);
        }
        return this._tabs;
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this._contextualData.notifyUpdated.unsubscribe(this._showTestTab);
    }

    private _showTestTab = () => {
        let artifact: IViewContextData = this._getArtifactData(this._contextualData);
        if (this._tabs.value.length == 0) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._checkAndAddTab(artifact, contributionService, false);
        }
        else {
            this._tab.render = () => this._getTestResultsView(artifact);
        }
    }

    private _checkAndAddTab(artifact: IViewContextData, contributionService: IVssContributionService, isCompleted: boolean) {
        let state = { "state": "Completed" };
        if(!isCompleted)
        {
            state = { "state": "InProgress" };
        }

        if(!this._shouldUsePipelineAPi()) {
            const promise = contributionService.getDataAsync<TCMContracts.TestResultSummary>(this._buildSummaryDataProviderId, state, true);
            promise.then((data: TCMContracts.TestResultSummary) => {
                if (data && data.totalRunsCount > 0) {
                    this._tab.render = () => this._getTestResultsView(artifact);
                    this._tabs.push(this._tab);
                }

            });
        }
        else {
            const promise = contributionService.getDataAsync<TCMContracts.PipelineTestMetrics>(this._pipelineSummaryDataProviderId, state, true);
            promise.then((data: TCMContracts.PipelineTestMetrics) => {
                if (data && data.runSummary && data.runSummary.totalRunsCount > 0) {
                    this._tab.render = () => this._getTestResultsView(artifact);
                    this._tabs.push(this._tab);
                }
            });
        }
    }

    private _getTestResultsView(artifact: IViewContextData): JSX.Element
    {
        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {

                    return <div className="testtab-pipeline-extension"
                            ref = {(testResultsViewContainer: HTMLDivElement) => (this._resultsContainerRef = testResultsViewContainer)}>
                                <TestResultsView artifactData={artifact} 
                                    widthAvailableToRender={this._resultsContainerRef ? this._resultsContainerRef.clientWidth : undefined } />
                            </div>
                }}
            </ScreenSizeObserver>
        );
    }

    private _getArtifactData(props: IRunViewContributedTabProps): IViewContextData {
        const mainData = {
            id: props.runId.value,
            sourceBranch: props.sourceBranch.value,
            repository: {
                id: props.sourceRepositoryId.value,
                type: props.sourceRepositoryType.value
            },
            definition: {
                id: props.pipelineId.value
            },
            stages: props.stages.value,
            status: props.status.value
        };
        const status: ViewContextStatus = this._getPipelineStatus(props.status.value);
        const artifactData: IViewContextData = {
            viewContext: ViewContext.Build,
            data: { mainData: mainData } as IData,
            status: status,
            contributionSource: ContributionSource.Pipeline
        };

        return artifactData;
    }

    private _getPipelineStatus(status: BuildStatus): ViewContextStatus {
        if (status === BuildStatus.InProgress) {
            return ViewContextStatus.InProgress;
        } else if (status === BuildStatus.Completed) {
            return ViewContextStatus.Completed;
        } else {
            return ViewContextStatus.Others;
        }
    }

    private _shouldUsePipelineAPi(): boolean {
        return isFeatureFlagEnabled(this.pageContext, this._metricsAPIInUnifiedPipeline, false);
    }

    private _resultsContainerRef: any;
    private _contextualData: IRunViewContributedTabProps;
    private _tabs = new ObservableArray<IVssContributedTab>();
    private readonly _pipelineSummaryDataProviderId = "ms.vss-test-web.test-tab-unifiedPipeline-summary-data-provider";
    private readonly _buildSummaryDataProviderId = "ms.vss-test-web.test-tab-build-summary-data-provider";
    private readonly _metricsAPIInUnifiedPipeline: string = "WebAccess.TestManagement.UnifiedPipelineMetricsAPI";
    private _tab: IVssContributedTab = {
        id: "ms.vss-test-web.unified-test-results-tab",
        name: Resources.TestTabText,
        order: 2
    };

}

Services.add("test-results-provider-service", { serviceFactory: TestResultsTabProviderService });